import { useEffect, useRef } from 'react';
import { Box, IconButton } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

const ACCENT = '#2C3E2D';

const turnButtonSx = {
  width: 32,
  height: 32,
  borderRadius: 0,
  border: '1px solid #E4DFD9',
  color: '#6B6560',
  bgcolor: '#F5F3F0',
  flexShrink: 0,
  '&:hover': { bgcolor: ACCENT, color: '#fff', borderColor: ACCENT },
  '&.Mui-disabled': { bgcolor: '#F5F3F0', color: '#C4BEB8', borderColor: '#E4DFD9' },
};

function screenLetter(index: number): string {
  let n = index;
  let letters = '';
  do {
    letters = String.fromCharCode(65 + (n % 26)) + letters;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return letters;
}

/**
 * One box per screen of products, lettered A, B, C, and onward.
 */
export default function MenuLeaves({
  count,
  active,
  onSelect,
}: {
  count: number;
  active: number;
  onSelect: (index: number) => void;
}) {
  const activeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    activeRef.current?.scrollIntoView({ inline: 'nearest', block: 'nearest' });
  }, [active]);

  return (
    <Box
      data-tour="menu-leaves"
      sx={{
        ml: 'auto',
        minWidth: 0,
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
      }}
    >
      <IconButton
        aria-label="Previous screen of products"
        size="small"
        disabled={active <= 0}
        onClick={() => onSelect(active - 1)}
        sx={turnButtonSx}
      >
        <ChevronLeftIcon sx={{ fontSize: 16 }} />
      </IconButton>
      <Box
        sx={{
          minWidth: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          overflowX: 'auto',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        {Array.from({ length: count }, (_, index) => {
          const selected = index === active;
          const letter = screenLetter(index);
          return (
            <Box
              key={letter}
              ref={selected ? activeRef : undefined}
              component="button"
              type="button"
              onClick={() => onSelect(index)}
              aria-label={`Screen ${letter}`}
              aria-current={selected ? 'page' : undefined}
              sx={{
                width: 32,
                height: 32,
                flexShrink: 0,
                p: 0,
                border: '1px solid',
                borderColor: selected ? ACCENT : '#E4DFD9',
                borderRadius: 0,
                bgcolor: selected ? ACCENT : '#FFF',
                color: selected ? '#FFF' : '#1A1A1A',
                cursor: 'pointer',
                font: 'inherit',
                fontSize: '0.75rem',
                fontWeight: 700,
                lineHeight: 1,
                '&:hover': {
                  borderColor: ACCENT,
                  bgcolor: selected ? ACCENT : '#F5F3F0',
                },
              }}
            >
              {letter}
            </Box>
          );
        })}
      </Box>
      <IconButton
        aria-label="Next screen of products"
        size="small"
        disabled={active >= count - 1}
        onClick={() => onSelect(active + 1)}
        sx={turnButtonSx}
      >
        <ChevronRightIcon sx={{ fontSize: 16 }} />
      </IconButton>
    </Box>
  );
}
